(function () {
  'use strict';

  const canvas = document.createElement('canvas');
  canvas.id = 'sky-canvas';
  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  let W, H;

  function resize() { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight; }

  /* ── Sky states: bright day → golden hour → sunset → night ── */
  const STATES = [
    { at: 0.00, stops: ['#1a6abf', '#2196f3', '#52b0f5', '#9dd0f8', '#cce8ff'] }, // clear morning
    { at: 0.20, stops: ['#0d47a1', '#1565c0', '#2196f3', '#64b5f6', '#b3d8f8'] }, // midday deep blue
    { at: 0.45, stops: ['#1a237e', '#4a3080', '#b86820', '#e8a030', '#f8d060'] }, // golden hour
    { at: 0.63, stops: ['#0a0618', '#2a0e3c', '#8b1820', '#c83018', '#e85010'] }, // sunset
    { at: 0.78, stops: ['#050210', '#110626', '#1c0828', '#23081c', '#2c0a14'] }, // dusk
    { at: 1.00, stops: ['#000005', '#02001a', '#040015', '#060013', '#080016'] }, // full night
  ];

  /* ── 220 stars, upper 65% of sky ── */
  const STARS = Array.from({ length: 220 }, () => ({
    x:     Math.random(),
    y:     Math.random() * 0.65,
    r:     0.4 + Math.random() * 1.1,
    phase: Math.random() * Math.PI * 2,
    speed: 0.018 + Math.random() * 0.028,
  }));

  /* ── Moon (appears in dusk/night) ── */
  const MOON = { phase: 0 };

  /* ── Helpers ── */
  function hex2rgb(h) {
    return [parseInt(h.slice(1,3),16), parseInt(h.slice(3,5),16), parseInt(h.slice(5,7),16)];
  }
  function lerp(a, b, t) { return a + (b - a) * t; }
  function lerpColor(h1, h2, t) {
    const a = hex2rgb(h1), b = hex2rgb(h2);
    return `rgb(${lerp(a[0],b[0],t)|0},${lerp(a[1],b[1],t)|0},${lerp(a[2],b[2],t)|0})`;
  }
  function ease(t) { return t<0.5 ? 2*t*t : -1+(4-2*t)*t; }

  /* ── Gradient for current scroll ── */
  function buildSkyGrad(p) {
    let i = STATES.length - 2;
    for (let k = 0; k < STATES.length-1; k++) {
      if (p <= STATES[k+1].at) { i = k; break; }
    }
    const from = STATES[i], to = STATES[i+1];
    const t = ease(Math.max(0, Math.min(1, (p - from.at) / (to.at - from.at))));
    const grad = ctx.createLinearGradient(0, 0, 0, H);
    const POS  = [0, 0.22, 0.50, 0.76, 1];
    from.stops.forEach((c, idx) => grad.addColorStop(POS[idx], lerpColor(c, to.stops[idx], t)));
    return grad;
  }

  /* ── Sun ──────────────────────────────────────────
     Visible from p=0 → p≈0.68. Arc from upper-left
     to right horizon via a high-noon peak at p=0.25. */
  function drawSun(p) {
    if (p > 0.70) return;
    const fadeOut = p > 0.55 ? 1 - (p - 0.55) / 0.15 : 1;
    if (fadeOut <= 0) return;

    const t  = p / 0.68;                               // 0→1 over visible span
    const sx = lerp(W * 0.10, W * 0.90, t);
    // Parabolic arc: starts 22%, peaks 8%, ends 78% down viewport
    const sy = H * (0.22 + 0.56 * t - 1.70 * t * (1 - t));

    // Color shifts from warm white → golden → deep orange
    const cr = 255;
    const cg = p < 0.45 ? 245 : Math.max(120, lerp(245, 80, (p-0.45)/0.25)) | 0;
    const cb = p < 0.30 ? 180 : Math.max(0,   lerp(180, 0,  (p-0.30)/0.25)) | 0;

    // Outer atmospheric halo
    const halo = ctx.createRadialGradient(sx, sy, 0, sx, sy, H * 0.38);
    halo.addColorStop(0,   `rgba(${cr},${cg},${cb},${0.07 * fadeOut})`);
    halo.addColorStop(0.4, `rgba(${cr},${cg},${cb},${0.03 * fadeOut})`);
    halo.addColorStop(1,   `rgba(${cr},${cg},${cb},0)`);
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(sx, sy, H * 0.38, 0, Math.PI * 2);
    ctx.fill();

    // Inner corona
    const corona = ctx.createRadialGradient(sx, sy, 0, sx, sy, 55);
    corona.addColorStop(0,   `rgba(${cr},${cg},${cb},${0.55 * fadeOut})`);
    corona.addColorStop(0.5, `rgba(${cr},${cg},${cb},${0.18 * fadeOut})`);
    corona.addColorStop(1,   `rgba(${cr},${cg},${cb},0)`);
    ctx.fillStyle = corona;
    ctx.beginPath();
    ctx.arc(sx, sy, 55, 0, Math.PI * 2);
    ctx.fill();

    // Sun disc
    ctx.beginPath();
    ctx.arc(sx, sy, 14 + t * 6, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${cr},${cg},${cb},${0.92 * fadeOut})`;
    ctx.fill();
  }

  /* ── Shooting stars ───────────────────────────── */
  const SHOOTERS = [];

  function maybeSpawnShooter(p) {
    if (p < 0.55) return;
    const intensity = Math.min(1, (p - 0.55) / 0.25);
    if (Math.random() > 0.022 * intensity) return;
    SHOOTERS.push({
      x:     W * 0.05 + Math.random() * W * 0.70,
      y:     Math.random() * H * 0.45,
      len:   140 + Math.random() * 180,
      vx:    14 + Math.random() * 10,
      vy:    5  + Math.random() * 7,
      life:  1.0,
      decay: 0.010 + Math.random() * 0.010,
    });
  }

  function drawShooters(p) {
    if (p < 0.55) { SHOOTERS.length = 0; return; }
    const fade = Math.min(1, (p - 0.55) / 0.06);
    for (let i = SHOOTERS.length - 1; i >= 0; i--) {
      const s = SHOOTERS[i];
      s.x += s.vx; s.y += s.vy;
      s.life -= s.decay;
      if (s.life <= 0 || s.x > W + 50) { SHOOTERS.splice(i, 1); continue; }
      const a = s.life * fade;
      const mag = Math.sqrt(s.vx * s.vx + s.vy * s.vy);
      const tx = s.x - (s.vx / mag) * s.len;
      const ty = s.y - (s.vy / mag) * s.len;
      const g = ctx.createLinearGradient(tx, ty, s.x, s.y);
      g.addColorStop(0,    `rgba(255,255,255,0)`);
      g.addColorStop(0.55, `rgba(200,225,255,${a * 0.45})`);
      g.addColorStop(1,    `rgba(255,255,255,${a})`);
      ctx.save();
      ctx.strokeStyle = g;
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(tx, ty); ctx.lineTo(s.x, s.y); ctx.stroke();
      const tipGlow = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, 10);
      tipGlow.addColorStop(0, `rgba(255,255,255,${a * 0.9})`);
      tipGlow.addColorStop(1, `rgba(255,255,255,0)`);
      ctx.fillStyle = tipGlow;
      ctx.beginPath(); ctx.arc(s.x, s.y, 10, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
    }
  }

  /* ── Moon ─────────────────────────────────────── */
  function drawMoon(p) {
    if (p < 0.72) return;
    const fadeIn = Math.min(1, (p - 0.72) / 0.16);

    MOON.phase += 0.0003;
    const mx = W * 0.72 + Math.sin(MOON.phase) * W * 0.06;
    const my = H * 0.12;

    // Soft glow
    const glow = ctx.createRadialGradient(mx, my, 0, mx, my, 80);
    glow.addColorStop(0, `rgba(200,215,255,${0.12 * fadeIn})`);
    glow.addColorStop(1, `rgba(200,215,255,0)`);
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(mx, my, 80, 0, Math.PI * 2);
    ctx.fill();

    // Disc
    ctx.beginPath();
    ctx.arc(mx, my, 18, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(220,230,250,${0.88 * fadeIn})`;
    ctx.fill();

    // Crescent shadow
    ctx.beginPath();
    ctx.arc(mx + 7, my - 2, 17, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(0,0,0,${0.75 * fadeIn})`;
    ctx.fill();
  }

  /* ── Horizon bloom (sunrise effect at day, glow at sunset) ── */
  function drawBloom(p) {
    let intensity = 0, col = '';
    if (p > 0.38 && p < 0.68) {
      intensity = Math.sin(Math.PI * (p - 0.38) / 0.30);
      col = '255,160,50';
    } else if (p > 0.60 && p < 0.82) {
      intensity = Math.sin(Math.PI * (p - 0.60) / 0.22) * 0.7;
      col = '255,70,15';
    }
    if (intensity < 0.02) return;
    const bloom = ctx.createRadialGradient(W*0.5, H, 0, W*0.5, H, W*0.75);
    bloom.addColorStop(0, `rgba(${col},${0.22 * intensity})`);
    bloom.addColorStop(1, `rgba(${col},0)`);
    ctx.fillStyle = bloom;
    ctx.fillRect(0, 0, W, H);
  }

  /* ── Star opacity curve ── */
  function starOpacity(p) {
    if (p < 0.42)  return 0;
    if (p < 0.65)  return lerp(0, 1, (p - 0.42) / 0.23);
    return 1;
  }

  /* ── Render loop ── */
  let frame = 0;
  function draw() {
    frame++;
    const maxS = document.documentElement.scrollHeight - window.innerHeight;
    const p    = maxS > 0 ? Math.min(1, window.scrollY / maxS) : 0;

    ctx.fillStyle = buildSkyGrad(p);
    ctx.fillRect(0, 0, W, H);

    drawBloom(p);
    drawSun(p);
    maybeSpawnShooter(p);
    drawShooters(p);
    drawMoon(p);

    const sAlpha = starOpacity(p);
    if (sAlpha > 0.01) {
      STARS.forEach(s => {
        const twinkle = 0.55 + 0.45 * Math.sin(frame * s.speed + s.phase);
        const a = sAlpha * twinkle;
        if (a < 0.02) return;
        const px = s.x * W, py = s.y * H;
        if (a > 0.4 && s.r > 1.1) {
          const g = ctx.createRadialGradient(px, py, 0, px, py, s.r * 5);
          g.addColorStop(0, `rgba(200,215,255,${a*0.35})`);
          g.addColorStop(1, 'rgba(200,215,255,0)');
          ctx.fillStyle = g; ctx.beginPath(); ctx.arc(px, py, s.r*5, 0, Math.PI*2); ctx.fill();
        }
        ctx.fillStyle = `rgba(215,230,255,${a})`;
        ctx.beginPath(); ctx.arc(px, py, s.r * twinkle, 0, Math.PI*2); ctx.fill();
      });
    }

    requestAnimationFrame(draw);
  }

  window.addEventListener('resize', resize, { passive: true });
  resize();
  requestAnimationFrame(draw);
})();
